import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AssignmentPicker from './AssignmentPicker';
import { configApi, type AssignmentDefaults } from '../api/config';
import { useAuth } from '../context/AuthContext';
import { getApiErrorMessage } from '../types/api';

type EntityKey = keyof AssignmentDefaults;

const ROWS: { key: EntityKey; label: string; hint: string }[] = [
  {
    key: 'verfahren',
    label: 'Verfahren',
    hint: 'Wird zugewiesen, wenn ein neues Verfahren erstellt wird (manuell oder automatisch aus einem Inserat).',
  },
  {
    key: 'anfrage',
    label: 'Anfragen',
    hint: 'Wird zugewiesen, wenn eine neue Anfrage erfasst wird.',
  },
  {
    key: 'inserat',
    label: 'Inserate',
    hint: 'Wird zugewiesen, wenn die Aktualisierung ein neues Inserat einliest.',
  },
  {
    key: 'adresse',
    label: 'Adressen',
    hint: 'Wird zugewiesen, wenn eine neue Adresse manuell angelegt wird.',
  },
];

const EMPTY: AssignmentDefaults = {
  verfahren: null,
  anfrage: null,
  inserat: null,
  adresse: null,
};

export default function AssignmentDefaultsEditor() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canEdit = user?.role === 'city_admin' || user?.role === 'system_admin';

  const { data, isLoading, error } = useQuery({
    queryKey: ['config', 'assignment-defaults'],
    queryFn: configApi.getAssignmentDefaults,
  });

  const [draft, setDraft] = useState<AssignmentDefaults>(EMPTY);
  const [errorMsg, setErrorMsg] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (data) setDraft({ ...EMPTY, ...data });
  }, [data]);

  const dirty = data
    ? (Object.keys(EMPTY) as EntityKey[]).some(
        (k) => (draft[k] ?? null) !== ((data as AssignmentDefaults)[k] ?? null),
      )
    : false;

  const mutation = useMutation({
    mutationFn: () => configApi.updateAssignmentDefaults(draft),
    onSuccess: (result) => {
      queryClient.setQueryData(['config', 'assignment-defaults'], result);
      setSavedAt(Date.now());
      setErrorMsg('');
    },
    onError: (err) => {
      setErrorMsg(getApiErrorMessage(err, 'Fehler beim Speichern'));
    },
  });

  if (isLoading) return <p className="text-sm text-gray-500">Lädt...</p>;
  if (error) return <p className="text-sm text-red-600">Fehler beim Laden.</p>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Standard-Zuständigkeiten greifen, wenn ein neues Element angelegt wird und kein
        Verantwortlicher explizit angegeben ist. Sie können später jederzeit überschrieben werden.
      </p>

      <div className="divide-y divide-gray-100">
        {ROWS.map((row) => (
          <div key={row.key} className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 items-start">
            <div>
              <p className="text-sm font-medium text-gray-700">{row.label}</p>
              <p className="text-xs text-gray-500 mt-1">{row.hint}</p>
            </div>
            <div className="sm:col-span-2 mt-2 sm:mt-0">
              <AssignmentPicker
                value={draft[row.key]}
                onChange={(uid) => setDraft((d) => ({ ...d, [row.key]: uid }))}
                allowUnassign
                disabled={!canEdit}
              />
            </div>
          </div>
        ))}
      </div>

      {errorMsg && <p className="text-sm text-red-600">{errorMsg}</p>}

      {canEdit && (
        <div className="flex items-center gap-3">
          <button
            onClick={() => mutation.mutate()}
            disabled={!dirty || mutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {mutation.isPending ? 'Wird gespeichert...' : 'Speichern'}
          </button>
          {savedAt && !dirty && (
            <span className="text-xs text-green-700">Gespeichert.</span>
          )}
        </div>
      )}
      {!canEdit && (
        <p className="text-xs text-gray-500">
          Nur Administrator:innen können diese Einstellung ändern.
        </p>
      )}
    </div>
  );
}
